import { AxiosInstancePlus } from '@tencent/oneid-request';
import {
  EMAIL_VERIFY_CODE_SCENE,
  THROTTLE_DEFAULT_SECOND,
  VERIFY_CODE_SCENE,
} from 'common/constants/Constants';
import { IPolicy, IResponseType } from 'common/constants/Types';
import { http } from 'common/http';
import type { ICaptchaReportParams } from 'common/utils/captchaRequest';
import captchaRequest from 'common/utils/captchaRequest';
import JSRuntime from 'common/utils/JSRuntime';
import { ILoginByMobile, ISendMfaSmsResponse } from '../type';

class AuthnApi {
  public axiosInstance: AxiosInstancePlus = http;

  public prefixURL = '/v1/authn';

  /**
   * authCode换登录态
   */
  getAuthCodeSession(data: Record<string, any>) {
    return this.axiosInstance.post(`${this.prefixURL}/auth_code/session`, data);
  }

  /** AD登录 */
  loginByAd(
    idpInstanceId: string,
    params: {
      account_id: string;
      credential: string;
      isTest?: boolean;
    },
    reportParams?: ICaptchaReportParams,
  ) {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(
          `${this.prefixURL}/ldap/${idpInstanceId}`,
          {
            ...params,
            captchaVerification,
          },
          { skipShowError: true },
        ),
      reportParams,
    );
  }

  /**
   * 获得租户初始配置数据
   */
  getSettings(params: { accountId: string }): Promise<any> {
    return this.axiosInstance.get(`${this.prefixURL}/settings`, { params, skipShowError: true });
  }

  /**
   * 获取开通的应用列表
   */
  getSSOApps(): Promise<any> {
    return this.axiosInstance.get(`${this.prefixURL}/apps`, { skipShowError: true });
  }

  loginByEmailOrDomain(params: {
    email?: string;
    domain?: string;
    accountId?: string;
    domainName?: string;
  }): Promise<any> {
    return this.axiosInstance.get(`${this.prefixURL}/sso`, { params, skipShowError: true });
  }

  /**
   * 手机号验证码登录接口
   *
   * @description url上可能携带account_id参数，用于指定登录的企业
   */
  loginByCode(body: {
    verificationSessionId: string;
    verificationCode: string;
    app?: string;
    handle_handshake_optional?: boolean;
    account_id?: string;
  }): Promise<IResponseType<ILoginByMobile>> {
    return captchaRequest((captchaVerification) =>
      this.axiosInstance.post(`${this.prefixURL}/sms`, { ...body, captchaVerification }),
    );
  }

  /**
   * 发送邮箱验证码
   */
  sendEmailVerificationCode(
    params: {
      email: string;
      event: EMAIL_VERIFY_CODE_SCENE;
    },
    reportParams?: ICaptchaReportParams,
  ): Promise<IResponseType<any>> {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(`${this.prefixURL}/credentials/email:send`, {
          ...params,
          captchaVerification,
        }),
      reportParams,
    );
  }

  /**
   * 邮箱验证码登录接口
   */
  loginByEmailCode(body: {
    verificationSessionId: string;
    verificationCode: string;
    app?: string;
    handle_handshake_optional?: boolean;
    account_id?: string;
  }): Promise<IResponseType<ILoginByMobile>> {
    return this.axiosInstance.post(`${this.prefixURL}/email`, body);
  }

  /**
   * 发送验证码
   * @param params 手机号和场景
   * @description 先弹出滑块验证，再发送验证码
   */
  sendVerificationCode(
    params: {
      mobile: string;
      event: VERIFY_CODE_SCENE;
    },
    reportParams?: ICaptchaReportParams,
  ): Promise<IResponseType<ISendMfaSmsResponse>> {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(`${this.prefixURL}/credentials/sms:send`, {
          ...params,
          captchaVerification,
        }),
      reportParams,
    );
  }

  /**
   * 登录企业
   * @param id 企业id
   */
  loginAccount(id: string, reportParams?: ICaptchaReportParams) {
    // 透传url上所有参数给接口
    const urlParams = JSRuntime.parseLocationSearch();
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(
          `${this.prefixURL}/account`,
          { id, app: JSRuntime.appFromUrl, captchaVerification, ...urlParams },
          {
            skipShowError: true,
          },
        ),
      reportParams,
    );
  }

  /** SSO账号密码登录 */
  loginEnterprise(
    params: { credential: string; accountId: string },
    reportParams?: ICaptchaReportParams,
  ) {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(
          `${this.prefixURL}/enterprise`,
          { ...params, captchaVerification },
          { skipShowError: true },
        ),
      reportParams,
    );
  }

  /** 用户名密码登录忘记密码 */
  sendEnterpriseSms(
    params: {
      accountId?: string;
      username?: string;
      event: string;
    },
    reportParams?: ICaptchaReportParams,
  ): Promise<IResponseType<{ verificationSessionId: string; expire?: number }>> {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(`${this.prefixURL}/enterprise/credentials/send`, {
          ...params,
          captchaVerification,
        }),
      reportParams,
    );
  }

  /** 用户名密码登录短信验证码验证 */
  verifyEnterpriseSms(params: {
    verificationSessionId: string;
    verificationCode: string;
    event: string;
  }): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/enterprise/credentials/verify`, params);
  }

  /** 用户名密码登录设置新密码 */
  setEnterprisePwd(credential: string): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/enterprise/password:reset`, {
      credential,
    });
  }

  /** 登录前获取当前登录用户命中的密码策略 */
  getPasswordPolicy(): Promise<IResponseType<{ complexity: IPolicy }>> {
    return this.axiosInstance.get(`${this.prefixURL}/account/user/passwordPolicy`);
  }

  /**
   * 接受邀请
   */
  acceptInvitation(
    handshakes: { handshakeId: string; mobile: string }[],
    app?: string,
    account_id?: string,
  ): Promise<IResponseType> {
    return this.axiosInstance.post(
      `${this.prefixURL}/handshakes:accept`,
      {
        handshakes,
        app,
        account_id,
      },
      {
        throttleConfig: { wait: THROTTLE_DEFAULT_SECOND },
      },
    );
  }

  /**
   * 拒绝邀请
   */
  rejectInvitation(handshakeIds: string[], app?: string): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/handshakes:reject`, { handshakeIds, app });
  }

  /** 新拒绝邀请 */
  declineInvitation(handshakeIds: string[], app?: string): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/handshakes:decline`, { handshakeIds, app });
  }

  /** 获取邀请信息 */
  getInvitationInfo(accountId: string, handshakeId: string): Promise<IResponseType> {
    return this.axiosInstance.get(
      `${this.prefixURL}/accounts/${accountId}/handshakes/${handshakeId}`,
      { skipShowError: true },
    );
  }

  /** 短信链接中的cookie失效后，需要重新验证手机号 */
  resendHandshakeSms(
    params: { mobile: string },
    reportParams?: ICaptchaReportParams,
  ): Promise<IResponseType<{ stateToken: string }>> {
    return captchaRequest(
      (captchaVerification) =>
        this.axiosInstance.post(`${this.prefixURL}/handshakes/sms:reSend`, {
          ...params,
          captchaVerification,
        }),
      reportParams,
    );
  }

  /** 短信链接二次验证手机号的校验 */
  reverifyHandshakeSms(
    params: {
      handshake_id: string;
      verification_code: string;
      state_token: string;
    },
    configs?: Record<string, any>,
  ) {
    return this.axiosInstance.post(`${this.prefixURL}/handshakes/sms:reVerify`, params, configs);
  }

  /** 手机验证码验证，非登录 */
  verifyCode(body: {
    verificationSessionId: string;
    verificationCode: string;
    event?: VERIFY_CODE_SCENE;
  }): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/credentials/sms:verify`, body, {
      skipShowError: true,
    });
  }

  /** 设置邮件密码，邀请页用 */
  setEmailPassword(data: {
    password: string;
    event?: string;
    app?: string;
    email?: string;
  }): Promise<IResponseType> {
    return this.axiosInstance.post(`${this.prefixURL}/credentials/email_password:set`, data);
  }

  /** 接受邮箱邀请 */
  acceptEmailInvitation(handshakeId: string, email: string): Promise<IResponseType> {
    return this.axiosInstance.post(
      `${this.prefixURL}/handshake/${handshakeId}:accept`,
      { email },
      {
        skipShowError: true,
      },
    );
  }

  /** 邀请加入成功后的前往使用地址 */
  handshakeTakeEffect(): Promise<IResponseType<{ redirectUrl: string }>> {
    return this.axiosInstance.post(
      `${this.prefixURL}/handshakes:take_effect`,
      {},
      { skipShowError: true },
    );
  }
}
export default new AuthnApi();
